import { Component, type ErrorInfo, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

type Props = { children: ReactNode };
type State = { hasError: boolean; error: Error | null };

export class AppErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (import.meta.env.DEV) {
      console.error("AppErrorBoundary:", error, errorInfo.componentStack);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4 py-16 text-center">
          <h1 className="text-2xl font-display font-semibold text-foreground mb-2">Something went wrong</h1>
          <p className="text-muted-foreground max-w-md mb-6">
            Please try again. If the problem continues, return home and continue browsing.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Button type="button" onClick={() => this.setState({ hasError: false, error: null })}>
              Try again
            </Button>
            <Button type="button" variant="outline" asChild>
              <Link to="/">Back to home</Link>
            </Button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
